import { Box, Chip, Tooltip } from "@mui/material";
import { FC } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";

export type TQuickFilterId =
  | "vencimento_mes"
  | "recebimento_mes"
  | "vencido_mes"
  | "atrasado_sem_receber";

interface IQuickFiltersParcelas {
  activeFilters: TQuickFilterId[];
  onFilterToggle: (filterId: TQuickFilterId) => void;
  onClearFilters: () => void;
}

interface IQuickFilterOption {
  id: TQuickFilterId;
  label: string;
  tooltip: string;
  icon: React.ReactNode;
  color: "primary" | "success" | "error" | "warning" | "default";
}

const quickFilterOptions: IQuickFilterOption[] = [
  {
    id: "vencimento_mes",
    label: "Vencimento este mês",
    tooltip: "Parcelas com vencimento neste mês",
    icon: <CalendarMonthIcon sx={{ fontSize: "1rem" }} />,
    color: "primary",
  },
  {
    id: "recebimento_mes",
    label: "Recebimento este mês",
    tooltip: "Parcelas recebidas neste mês",
    icon: <EventAvailableIcon sx={{ fontSize: "1rem" }} />,
    color: "success",
  },
  {
    id: "vencido_mes",
    label: "Vencidas este mês",
    tooltip: "Parcelas vencidas neste mês (não recebidas)",
    icon: <WarningAmberIcon sx={{ fontSize: "1rem" }} />,
    color: "warning",
  },
  {
    id: "atrasado_sem_receber",
    label: "Atrasado nos últimos 30 dias",
    tooltip: "Parcelas vencidas há mais de 30 dias sem receber",
    icon: <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />,
    color: "error",
  },
];

export const QuickFiltersParcelas: FC<IQuickFiltersParcelas> = ({
  activeFilters,
  onFilterToggle,
  onClearFilters,
}) => {
  const isActive = (filterId: TQuickFilterId) => activeFilters.includes(filterId);
  const hasActiveFilters = activeFilters.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {quickFilterOptions.map((option) => {
        const active = isActive(option.id);
        return (
          <Tooltip key={option.id} title={option.tooltip} arrow>
            <Chip
              icon={option.icon as React.ReactElement}
              label={option.label}
              color={active ? option.color : "default"}
              variant={active ? "filled" : "outlined"}
              onClick={() => onFilterToggle(option.id)}
              sx={{
                cursor: "pointer",
                fontWeight: active ? 600 : 400,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                },
              }}
            />
          </Tooltip>
        );
      })}

      {hasActiveFilters && (
        <Tooltip title="Limpar todos os filtros rápidos" arrow>
          <Chip
            icon={<RefreshIcon sx={{ fontSize: "1rem" }} />}
            label={`Limpar (${activeFilters.length})`}
            variant="outlined"
            onClick={onClearFilters}
            sx={{
              cursor: "pointer",
              borderStyle: "dashed",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};
